import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getUser } from '../../utils/auth';
import {
  CalculatedValueCard,
  PreviewResult,
  applyLabel,
  emptyNamed,
  emptyTerm,
  retarget,
  uniqueKey,
} from '../CalculationBuilder';
import AccountingEditor, { accountingFromDistribution, distributionFromAccounting } from './AccountingEditor';
import { destinationState, documentFromState, periodLabel, stateFromDocument } from './formulaShape';
import { PARAMETER_UNITS, distributionSummary, formulaExpression, projectCostPhrase, projectCostPolicy } from './productCopy';
import SourceScopeEditor, { budgetCatalog, slicesFromGroups } from './SourceScopeEditor';
import AppSelect, { useAppSelectDismiss } from './AppSelect';
import AccountantBuilder, { PublishSummary, publishedSummary } from './AccountantBuilder';
import { basisOf, basisTitle, needsAdvanced, percentEntry, percentStored } from './accountantModel';
import { calculationReady } from './calculationPrompt';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function withDestination(rows, patch) {
  if (!patch?.destination_cost_code_vuid) return rows || [];
  if (!rows?.length) return [{ id: uid(), label: 'Project costs', pairs: [], ...patch }];
  return rows.map((item, index) => (index === 0 ? { ...item, ...patch } : item));
}

function previewSlices(groups, source, combinations) {
  const chosen = slicesFromGroups(groups);
  const stated = (groups || []).find((group) => group.destination_cost_code_vuid && group.destination_cost_type_vuid);
  const ready = chosen.some((slice) => (slice.pairs || []).some((pair) => pair.cost_code_vuid && pair.cost_type_vuid));
  if (ready) return chosen;
  const rows = (combinations || []).filter((row) => !source?.cost_type_vuid || row.cost_type_vuid === source.cost_type_vuid);
  if (!rows.length) return chosen;
  return [{
    label: source?.cost_type_label || 'Project costs',
    pairs: rows.map((row) => ({
      cost_code_vuid: row.cost_code_vuid,
      cost_type_vuid: row.cost_type_vuid,
      cost_code_label: `${row.code || ''} — ${row.description || ''}`.replace(/ — $/, ''),
      cost_type_label: row.abbreviation || row.cost_type || '',
    })),
    destination_cost_code_vuid: stated?.destination_cost_code_vuid || '',
    destination_cost_type_vuid: stated?.destination_cost_type_vuid || '',
    destination_cost_code_label: stated?.destination_cost_code_label || '',
    destination_cost_type_label: stated?.destination_cost_type_label || '',
  }];
}

export default function CalculationEditor() {
  const { vuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const inPreview = location.pathname.startsWith('/procore/');
  const isAdmin = (getUser()?.role || 'user') === 'admin';
  const forProject = new URLSearchParams(location.search).get('forProject') || '';
  const libraryPath = inPreview ? '/procore/setup/calculation-library' : '/calculation-library';
  const projectCalculationsPath = forProject
    ? (inPreview ? `/procore/projects/${forProject}/calculations` : `/projects/${forProject}/calculations`)
    : '';

  const [detail, setDetail] = useState(null);
  const [advanced, setAdvanced] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parameters, setParameters] = useState([]);
  const [calculationType, setCalculationType] = useState('periodic');
  const [namedValues, setNamedValues] = useState([emptyNamed()]);
  const [accountingLines, setAccountingLines] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [costTypes, setCostTypes] = useState([]);
  const [source, setSource] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [providers, setProviders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [budgetCombinations, setBudgetCombinations] = useState([]);
  const [projectVuid, setProjectVuid] = useState('');
  const [projectCodesOnly, setProjectCodesOnly] = useState(false);
  const [periodVuid, setPeriodVuid] = useState('');
  const [previewParameters, setPreviewParameters] = useState({});
  const [groups, setGroups] = useState([]);
  const [expandedGroupIds, setExpandedGroupIds] = useState(() => new Set());
  const [runInputValues, setRunInputValues] = useState({});
  const [testJobCostChoices, setTestJobCostChoices] = useState({});
  const [duplicateName, setDuplicateName] = useState('');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmingPublish, setConfirmingPublish] = useState(false);
  const [openVersion, setOpenVersion] = useState('');
  const [menu, setMenu] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [forceAsk, setForceAsk] = useState(false);
  const [projectCommitments, setProjectCommitments] = useState(null);
  const [excludedCommitments, setExcludedCommitments] = useState([]);
  useAppSelectDismiss(setMenu);
  const ready = !forceAsk && calculationReady({ parameters, namedValues, accountingLines });
  const commitmentBasis = basisOf(namedValues) === 'total_commitment_amount';

  const draft = (detail?.versions || []).find((version) => version.status === 'draft');
  const published = [...(detail?.versions || [])].reverse().find((version) => version.status === 'published');
  const editing = isAdmin && draft;
  const shownVersion = draft || published;

  const load = () => axios.get(`${baseURL}/api/calculation-library/${vuid}`).then((response) => {
    const body = response.data;
    setDetail(body);
    setName(body.name || '');
    setDescription(body.description || '');
    const version = (body.versions || []).find((item) => item.status === 'draft')
      || [...(body.versions || [])].reverse().find((item) => item.status === 'published');
    const state = stateFromDocument(version?.document || {});
    setParameters(state.parameters);
    setCalculationType(state.calculationType);
    setNamedValues(state.namedValues);
      setAccountingLines(accountingFromDistribution(version?.distribution));
      setSource(version?.document?.source || null);
      setGroups((rows) => withDestination(rows, forProject ? destinationState(version?.document) : null));
      return body;
  });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${baseURL}/api/calculation-library/${vuid}`),
      axios.get(`${baseURL}/api/calculations/providers`),
      axios.get(`${baseURL}/api/projects?per_page=100&lite=1`),
      axios.get(`${baseURL}/api/accounting-periods`),
      axios.get(`${baseURL}/api/chartofaccounts`),
      axios.get(`${baseURL}/api/cost-types`).catch(() => ({ data: [] })),
    ]).then(([detailRes, providerRes, projectRes, periodRes, accountRes, typeRes]) => {
      if (cancelled) return;
      const body = detailRes.data;
      setDetail(body);
      setName(body.name || '');
      setDescription(body.description || '');
      const version = (body.versions || []).find((item) => item.status === 'draft')
        || [...(body.versions || [])].reverse().find((item) => item.status === 'published');
      const state = stateFromDocument(version?.document || {});
      setParameters(state.parameters);
      setCalculationType(state.calculationType);
      setNamedValues(state.namedValues);
      setAdvanced(needsAdvanced(state.namedValues));
      setAccountingLines(accountingFromDistribution(version?.distribution));
      setSource(version?.document?.source || null);
      setGroups((rows) => withDestination(rows, forProject ? destinationState(version?.document) : null));
      setAccounts(Array.isArray(accountRes.data) ? accountRes.data.filter((account) => account.status === 'active') : []);
      setCostTypes(Array.isArray(typeRes.data) ? typeRes.data : []);
      setProviders(providerRes.data?.providers || []);
      const projectRows = Array.isArray(projectRes.data) ? projectRes.data : (projectRes.data?.items || []);
      setProjects(projectRows);
      const preferred = projectRows.find((row) => row.vuid === forProject);
      if (preferred) setProjectVuid(preferred.vuid);
      else if (projectRows[0]) setProjectVuid(projectRows[0].vuid);
      const periodRows = Array.isArray(periodRes.data) ? periodRes.data : [];
      setPeriods(periodRows);
      const open = periodRows.find((period) => String(period.status || '').toLowerCase() === 'open');
      if (open) setPeriodVuid(open.vuid);
    }).catch((err) => {
      if (!cancelled) setError(err.response?.data?.error || 'Could not load this calculation.');
    });
    return () => { cancelled = true; };
  }, [vuid]);

  useEffect(() => {
    if (!forProject || !commitmentBasis) {
      setProjectCommitments(null);
      return undefined;
    }
    let cancelled = false;
    axios.get(`${baseURL}/api/project-commitments`, { params: { project_vuid: forProject } }).then((response) => {
      if (!cancelled) setProjectCommitments(Array.isArray(response.data) ? response.data : []);
    }).catch(() => {
      if (!cancelled) setProjectCommitments([]);
    });
    return () => { cancelled = true; };
  }, [forProject, commitmentBasis]);

  useEffect(() => {
    if (!forProject || !vuid) return undefined;
    let cancelled = false;
    axios.get(`${baseURL}/api/projects/${forProject}/calculations`).then((response) => {
      if (cancelled) return;
      const assignment = (response.data.assignments || []).find((row) => row.definition_vuid === vuid);
      if (assignment) setExcludedCommitments(assignment.excluded_commitment_vuids || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [forProject, vuid]);

  useEffect(() => {
    if (!projectVuid) return undefined;
    let cancelled = false;
    axios.get(`${baseURL}/api/projects/${projectVuid}/settings`).then((response) => {
      if (!cancelled) setProjectCodesOnly(!!response.data?.allow_project_cost_codes);
    }).catch(() => {
      if (!cancelled) setProjectCodesOnly(false);
    });
    axios.get(`${baseURL}/api/projects/${projectVuid}/budget-lines`).then((response) => {
      if (cancelled) return;
      setBudgetCombinations(budgetCatalog(response.data).combinations);
    }).catch(() => {
      if (!cancelled) setBudgetCombinations([]);
    });
    return () => { cancelled = true; };
  }, [projectVuid]);

  const referencedRunProviders = useMemo(() => {
    const keys = new Set();
    namedValues.forEach((named) => named.terms.forEach((term) => {
      if (term.kind === 'input' && term.key) keys.add(term.key);
    }));
    return providers.filter((provider) => keys.has(provider.key) && provider.evaluation_behavior === 'run_entered');
  }, [namedValues, providers]);

  const referencedScoped = useMemo(() => {
    const keys = new Set();
    namedValues.forEach((named) => named.terms.forEach((term) => {
      if (term.kind === 'input' && term.key) keys.add(term.key);
    }));
    return providers.filter((provider) => keys.has(provider.key) && provider.evaluation_behavior === 'scoped');
  }, [namedValues, providers]);
  const scopeOptional = referencedScoped.length > 0 && referencedScoped.every((provider) => provider.empty_scope_is_unfiltered);

  const renameParameter = (id, label) => {
    const result = applyLabel(parameters, id, label);
    setParameters(result.rows.map((row) => (row.id === id ? { ...row, label } : row)));
    if (result.oldKey !== result.newKey) {
      setNamedValues((rows) => retarget(rows, 'parameter', result.oldKey, result.newKey));
    }
  };

  const renameCalculated = (id, label) => {
    const result = applyLabel(namedValues, id, label);
    setNamedValues(result.rows);
  };

  const updateNamed = (id, patch) => {
    setNamedValues((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const projectDestination = () => (
    forProject ? groups.find((group) => group.destination_cost_code_vuid) : null
  );

  const saveProjectSetup = async () => {
    if (!forProject) return null;
    const destination = groups.find((group) => group.destination_cost_code_vuid);
    const typeVuid = source?.cost_type_vuid || '';
    const pairs = (budgetCombinations || []).filter((row) => (
      row.cost_code_vuid && row.cost_type_vuid && (!typeVuid || row.cost_type_vuid === typeVuid)
    )).map((row) => ({
      cost_code_vuid: row.cost_code_vuid,
      cost_type_vuid: row.cost_type_vuid,
      cost_code_label: `${row.code || ''} — ${row.description || ''}`.replace(/ — $/, ''),
      cost_type_label: row.abbreviation || row.cost_type || '',
    }));
    if (!pairs.length && !destination) return null;
    const listed = await axios.get(`${baseURL}/api/projects/${forProject}/calculations`);
    const assignment = (listed.data.assignments || []).find((row) => row.definition_vuid === vuid);
    if (!assignment) return null;
    const response = await axios.put(`${baseURL}/api/projects/${forProject}/calculations/${assignment.vuid}`, {
      parameter_values: Object.fromEntries((parameters || []).filter((row) => row.key).map((row) => [row.key, row.defaultValue || ''])),
      slices: [{
        label: source?.cost_type_label || 'Project costs',
        pairs,
        destination_cost_code_vuid: destination?.destination_cost_code_vuid || '',
        destination_cost_type_vuid: destination?.destination_cost_type_vuid || typeVuid,
      }],
    });
    return response.data;
  };

  const saveCommitmentExclusions = async () => {
    if (!forProject || basisOf(namedValues) !== 'total_commitment_amount') return null;
    const listed = await axios.get(`${baseURL}/api/projects/${forProject}/calculations`);
    const assignment = (listed.data.assignments || []).find((row) => row.definition_vuid === vuid);
    if (!assignment) return null;
    const response = await axios.put(`${baseURL}/api/projects/${forProject}/calculations/${assignment.vuid}`, {
      excluded_commitment_vuids: excludedCommitments,
    });
    setExcludedCommitments(response.data.excluded_commitment_vuids || []);
    return response.data;
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${baseURL}/api/calculation-library/${vuid}`, { name, description });
      let version = draft;
      if (!version) {
        const opened = await axios.post(`${baseURL}/api/calculation-library/${vuid}/draft`);
        version = opened.data;
      }
      await axios.put(`${baseURL}/api/calculation-library/versions/${version.vuid}`, {
        document: documentFromState(parameters, namedValues, source, projectDestination(), calculationType),
        distribution: distributionFromAccounting(accountingLines, namedValues),
      });
      if (forProject) {
        try {
          await axios.post(`${baseURL}/api/projects/${forProject}/calculations`, { definition_vuid: vuid });
        } catch (assignErr) {
          const message = assignErr.response?.data?.error || '';
          if (!/already assigned/i.test(message)) {
            setError(message || 'Saved the draft, but it could not be added to this project.');
            await load();
            return;
          }
        }
      }
      await saveProjectSetup();
      await saveCommitmentExclusions();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the draft.');
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${baseURL}/api/calculation-library/${vuid}`, { name, description });
      let version = draft;
      if (!version) {
        const opened = await axios.post(`${baseURL}/api/calculation-library/${vuid}/draft`);
        version = opened.data;
      }
      await axios.put(`${baseURL}/api/calculation-library/versions/${version.vuid}`, {
        document: documentFromState(parameters, namedValues, source, projectDestination(), calculationType),
        distribution: distributionFromAccounting(accountingLines, namedValues),
      });
      await axios.post(`${baseURL}/api/calculation-library/versions/${version.vuid}/publish`);
      if (forProject) {
        try {
          await axios.post(`${baseURL}/api/projects/${forProject}/calculations`, { definition_vuid: vuid });
        } catch (assignErr) {
          const message = assignErr.response?.data?.error || '';
          if (!/already assigned/i.test(message)) {
            setError(message || 'Published to the library, but it could not be added to this project.');
          }
        }
        const stored = await saveProjectSetup();
        await saveCommitmentExclusions();
        if (stored?.vuid && projectCalculationsPath) {
          navigate(`${projectCalculationsPath}/${stored.vuid}`);
          return;
        }
      }
      setPreview(null);
      setConfirmingPublish(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not publish this version.');
    } finally {
      setSaving(false);
    }
  };

  const startDraft = async () => {
    setError('');
    try {
      await axios.post(`${baseURL}/api/calculation-library/${vuid}/draft`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start a draft.');
    }
  };

  const duplicate = async (event) => {
    event.preventDefault();
    setError('');
    try {
      const response = await axios.post(`${baseURL}/api/calculation-library/${vuid}/duplicate`, { name: duplicateName });
      navigate(inPreview
        ? `/procore/setup/calculation-library/${response.data.definition.vuid}`
        : `/calculation-library/${response.data.definition.vuid}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not duplicate this calculation.');
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${detail?.name || 'this calculation'}? It has not been assigned to any project.`)) return;
    setError('');
    try {
      await axios.delete(`${baseURL}/api/calculation-library/${vuid}`);
      navigate(libraryPath);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not delete this calculation.');
    }
  };

  const setStatus = async (status) => {
    setError('');
    try {
      await axios.put(`${baseURL}/api/calculation-library/${vuid}`, { status });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not change the status.');
    }
  };

  const runPreview = async () => {
    if (!shownVersion) return;
    setError('');
    setPreviewError('');
    setPreview(null);
    const slices = previewSlices(groups, source, budgetCombinations);
    const ready = slices.some((slice) => (slice.pairs || []).some((pair) => pair.cost_code_vuid && pair.cost_type_vuid));
    if (referencedScoped.length && !scopeOptional && !ready) {
      setPreviewError('Choose the cost codes this calculation should use, then run it again.');
      return;
    }
    const needsDestination = accountingLines.some((line) => line.side !== 'credit' && (line.job_cost_policy || 'never') === 'always');
    if (needsDestination && !slices.some((slice) => slice.destination_cost_code_vuid && slice.destination_cost_type_vuid)) {
      setPreviewError('Which cost code should the project cost use?');
      return;
    }
    try {
      const response = await axios.post(`${baseURL}/api/calculation-library/versions/${shownVersion.vuid}/preview`, {
        project_vuid: projectVuid,
        accounting_period_vuid: periodVuid,
        parameter_values: Object.fromEntries(parameters.filter((row) => row.key).map((row) => {
          const entered = previewParameters[row.key];
          if (entered == null || entered === '') return [row.key, row.defaultValue || ''];
          return [row.key, row.display === 'percent' ? percentStored(entered) : entered];
        })),
        slices,
        distribution: distributionFromAccounting(accountingLines, namedValues),
        job_cost_choices: testJobCostChoices,
        run_inputs: referencedRunProviders.map((provider) => ({
          key: provider.key,
          value: runInputValues[provider.key] || '',
        })),
      });
      const period = periods.find((item) => item.vuid === periodVuid);
      setPreview({ ...response.data, period_label: periodLabel(period) });
    } catch (err) {
      setPreviewError(err.response?.data?.error || 'Preview failed.');
    }
  };

  if (!detail) {
    return <div className="text-sm text-gray-600">{error || 'Loading…'}</div>;
  }

  const actions = isAdmin ? (
    <div className={inPreview ? 'pc-calc-actions' : 'flex gap-2'}>
      {!draft && published ? <button type="button" className="btn-claret" onClick={startDraft}>Create New Draft</button> : null}
      <button
        type="button"
        className="btn-claret"
        onClick={() => setStatus(detail.status === 'active' ? 'inactive' : 'active')}
      >
        {detail.status === 'active' ? 'Deactivate' : 'Activate'}
      </button>
    </div>
  ) : null;

  return (
    <div className={inPreview ? 'pc-calc pc-calc-editor' : 'max-w-4xl'}>
      {inPreview && ready && advanced ? actions : null}
      <div className="flex items-center justify-between gap-3 mb-2">
        <button type="button" className="text-sm underline" onClick={() => navigate(projectCalculationsPath || libraryPath)}>
          {projectCalculationsPath ? 'Back to Project Calculations' : 'Back to Calculation Library'}
        </button>
        {isAdmin && !detail.project_count ? (
          <button type="button" className="btn-claret" onClick={remove}>Delete</button>
        ) : null}
      </div>
      {ready && advanced ? <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{detail.name}</h1>
          <p className="text-sm text-gray-600">
            {draft ? `Draft v${draft.version_number}` : published ? `Published v${published.version_number}. Create a new draft to change it.` : 'No version yet'}
          </p>
        </div>
        {inPreview ? null : actions}
      </div> : null}
      {ready && advanced && forProject && draft ? (
        <p className="text-sm text-gray-600 mb-3">
          Describe it below, adjust the fields, then publish it for this project.
        </p>
      ) : null}
      {error ? <div className="mb-4 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div> : null}

      <AccountantBuilder
        editing={!!editing}
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
        parameters={parameters}
        setParameters={setParameters}
        namedValues={namedValues}
        setNamedValues={setNamedValues}
        accountingLines={accountingLines}
        setAccountingLines={setAccountingLines}
        accounts={accounts}
        costTypes={costTypes}
        source={source}
        setSource={setSource}
        costCodes={budgetCombinations}
        destination={groups.find((group) => group.destination_cost_code_vuid) || null}
        onDestination={(row) => {
          if (!row?.cost_code_vuid) return;
          const patch = {
            destination_cost_code_vuid: row.cost_code_vuid,
            destination_cost_type_vuid: row.cost_type_vuid || '',
            destination_cost_code_label: `${row.code || ''} — ${row.description || ''}`.replace(/ — $/, ''),
            destination_cost_type_label: row.abbreviation || row.cost_type || '',
          };
          setGroups((rows) => {
            if (!rows.length) return [{ id: uid(), label: 'Project costs', pairs: [], ...patch }];
            return rows.map((item, index) => (index === 0 ? { ...item, ...patch } : item));
          });
        }}
        showEquations={!advanced}
        asking={!ready && !advanced}
        onBuilt={() => setForceAsk(false)}
        onRestart={() => setForceAsk(true)}
        calculationType={calculationType}
        setCalculationType={setCalculationType}
        commitments={forProject && commitmentBasis ? projectCommitments : null}
        excludedCommitments={excludedCommitments}
        setExcludedCommitments={setExcludedCommitments}
      />
      {advanced && editing ? (
        <button type="button" className="text-sm underline mb-3" onClick={() => setAdvanced((value) => !value)}>
          Guided setup
        </button>
      ) : null}

      {advanced ? (
      <>
      <section className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Details</h2>
        <p className="text-sm text-gray-600 mb-3">What this calculation is. Projects do not change this name.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm font-medium text-gray-700">
            Name
            <input className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md" value={name} disabled={!isAdmin} onChange={(event) => setName(event.target.value)} />
          </label>
          <label className="text-sm font-medium text-gray-700">
            Description
            <input className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md" value={description} disabled={!isAdmin} onChange={(event) => setDescription(event.target.value)} />
          </label>
        </div>
        <p className="text-sm text-gray-500 mt-3">Status: {detail.status === 'active' ? 'Active' : 'Inactive'}. Inactive calculations stay readable and cannot be newly assigned.</p>
      </section>

      <section className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-900">Parameters</h2>
          {editing ? (
            <button
              type="button"
              className="btn-claret"
              onClick={() => setParameters((rows) => [...rows, { id: uid(), label: '', key: '', description: '', required: true, defaultValue: '', display: 'number' }])}
            >
              Add parameter
            </button>
          ) : null}
        </div>
        <p className="text-sm text-gray-600 mb-4">Reusable configuration values. Each project can use its own value. A default prefills a new project and does not change projects already assigned.</p>
        {parameters.map((row) => (
          <div key={row.id} className="border border-gray-200 rounded-md p-3 mb-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                className="px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Name"
                value={row.label}
                disabled={!editing}
                onChange={(event) => renameParameter(row.id, event.target.value)}
              />
              <label className="text-sm text-gray-700">
                Shown as
                <select
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={row.display || 'number'}
                  disabled={!editing}
                  onChange={(event) => setParameters((rows) => rows.map((item) => (item.id === row.id ? { ...item, display: event.target.value } : item)))}
                >
                  {PARAMETER_UNITS.map((unit) => (
                    <option key={unit.value} value={unit.value}>{unit.label}</option>
                  ))}
                </select>
              </label>
              <input
                className="px-3 py-2 border border-gray-300 rounded-md"
                placeholder={row.display === 'percent' ? 'Default decimal, such as 0.12 for 12%' : 'Default (optional)'}
                value={row.defaultValue}
                disabled={!editing}
                onChange={(event) => setParameters((rows) => rows.map((item) => (item.id === row.id ? { ...item, defaultValue: event.target.value } : item)))}
              />
              <input
                className="px-3 py-2 border border-gray-300 rounded-md md:col-span-2"
                placeholder="Description"
                value={row.description}
                disabled={!editing}
                onChange={(event) => setParameters((rows) => rows.map((item) => (item.id === row.id ? { ...item, description: event.target.value } : item)))}
              />
            </div>
            <label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={row.required !== false}
                disabled={!editing}
                onChange={(event) => setParameters((rows) => rows.map((item) => (item.id === row.id ? { ...item, required: event.target.checked } : item)))}
              />
              Required
            </label>
          </div>
        ))}
      </section>

      {referencedScoped.length ? (
        <p className="text-sm text-gray-700 mb-4">
          This method uses {referencedScoped.map((provider) => basisTitle(provider.key) || provider.name).join(', ')}. {scopeOptional
            ? 'Owner billing includes every posted cost code unless a project limits it to a smaller set. That limit is not stored on the library calculation.'
            : 'Each project chooses which costs are included. Those choices are not stored on the library calculation.'}
        </p>
      ) : null}

      <section className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-900">Calculation</h2>
          {editing ? (
            <button
              type="button"
              className="btn-claret"
              onClick={() => {
                const created = emptyNamed();
                setNamedValues((rows) => [...rows, created]);
                setRenamingId(created.id);
              }}
            >
              Add calculated value
            </button>
          ) : null}
        </div>
        <p className="text-sm text-gray-600 mb-4">How the amount is calculated. The formula reads in the order an accountant would describe it.</p>
        {namedValues.map((named) => (
          <CalculatedValueCard
            key={named.id}
            named={named}
            providers={providers}
            parameters={parameters}
            namedValues={namedValues}
            renaming={editing && (renamingId === named.id || !named.label)}
            menuOpen={menuId === named.id}
            onRename={(label) => renameCalculated(named.id, label)}
            onFinishRename={() => setRenamingId(null)}
            onToggleMenu={() => editing && setMenuId((current) => (current === named.id ? null : named.id))}
            onCloseMenu={() => setMenuId(null)}
            onBeginRename={() => { setRenamingId(named.id); setMenuId(null); }}
            onDuplicate={() => {
              if (!editing) return;
              const copy = {
                ...named,
                id: uid(),
                label: named.label ? `${named.label} copy` : '',
                key: uniqueKey(named.label ? `${named.label} copy` : 'copy', namedValues.map((item) => item.key).filter(Boolean)),
                terms: named.terms.map((term) => ({ ...term })),
                operators: [...named.operators],
              };
              setNamedValues((rows) => {
                const index = rows.findIndex((item) => item.id === named.id);
                const next = rows.slice();
                next.splice(index + 1, 0, copy);
                return next;
              });
              setMenuId(null);
            }}
            onDelete={() => {
              if (!editing) return;
              const usedByValue = namedValues.some((item) => item.id !== named.id && item.terms.some((term) => term.kind === 'named' && term.key === named.key));
              const usedByJournal = accountingLines.some((line) => line.amount_key === named.key);
              if (usedByValue || usedByJournal) {
                const where = [
                  usedByValue ? 'another calculated value' : '',
                  usedByJournal ? 'the accounting method' : '',
                ].filter(Boolean).join(' and ');
                setError(`${named.label || 'This calculated value'} is used by ${where}. Remove that use before deleting it.`);
                setMenuId(null);
                return;
              }
              setNamedValues((rows) => rows.filter((item) => item.id !== named.id));
              setMenuId(null);
            }}
            onChangeTerm={(termIndex, next) => {
              if (!editing) return;
              updateNamed(named.id, { terms: named.terms.map((item, index) => (index === termIndex ? next : item)) });
            }}
            onChangeOperator={(operatorIndex, value) => {
              if (!editing) return;
              const operators = named.operators.slice();
              operators[operatorIndex] = value;
              updateNamed(named.id, { operators });
            }}
            onRemoveTerm={(termIndex) => {
              if (!editing) return;
              if (named.terms.length === 1) {
                updateNamed(named.id, { terms: [emptyTerm()], operators: [] });
                return;
              }
              updateNamed(named.id, {
                terms: named.terms.filter((_, index) => index !== termIndex),
                operators: named.operators.filter((_, index) => index !== (termIndex === 0 ? 0 : termIndex - 1)),
              });
            }}
            onAddStep={() => editing && updateNamed(named.id, { terms: [...named.terms, emptyTerm()], operators: [...named.operators, 'mul'] })}
            locked={!editing}
          />
        ))}
      </section>

      <AccountingEditor
        lines={accountingLines}
        setLines={setAccountingLines}
        namedValues={namedValues}
        accounts={accounts}
        editing={!!editing}
      />
      </>
      ) : null}

      {ready && editing ? (
        <div className="mb-3 pc-calc-agent-save">
          <div className="flex gap-3">
            <button type="button" className="btn-claret" onClick={save} disabled={saving}>Save draft</button>
            <button type="button" className="btn-claret" onClick={() => setConfirmingPublish(true)} disabled={saving}>
              {published ? 'Publish Version' : 'Publish Calculation'}
            </button>
          </div>
          {confirmingPublish ? (
            <div className="mt-4 rounded border border-gray-300 p-4 max-w-xl">
              <h3 className="text-base font-semibold text-gray-900 mb-2">Publish {name || detail.name}?</h3>
              <PublishSummary summary={publishedSummary({ name, parameters, namedValues, accountingLines })} />
              <p className="text-sm text-gray-700 mt-3">Publishing makes this version available to projects. Projects already using an older version will not change automatically.</p>
              <p className="text-sm text-gray-700 mt-2">
                {forProject && !published
                  ? 'This project will use the published version. Other projects can add it from the library after that.'
                  : published
                    ? `Existing projects assigned to v${published.version_number} will remain on v${published.version_number} until updated.`
                    : 'Projects are not assigned automatically. Assign this version after it is published.'}
              </p>
              <div className="mt-4 flex gap-3">
                <button type="button" className="px-4 py-2 border border-gray-300 rounded-md text-sm" onClick={() => setConfirmingPublish(false)} disabled={saving}>Cancel</button>
                <button type="button" className="btn-claret" onClick={publish} disabled={saving}>{published ? 'Publish Version' : 'Publish Calculation'}</button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {ready ? <div className="pc-calc-agent-more">
      <details className="bg-white border border-gray-200 rounded-md p-4 mb-3">
        <summary className="text-sm font-semibold text-gray-900 cursor-pointer">Try this calculation</summary>
        <p className="text-sm text-gray-600 mb-3 mt-2">
          See how this calculation would work using a real project and accounting period. Nothing will be saved or posted.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <label className="text-sm font-medium text-gray-700">
            Project
            <span className="mt-1 block font-normal">
              <AppSelect
                id="try-project"
                open={menu === 'try-project'}
                setOpen={setMenu}
                menuClassName="w-full"
                value={(() => {
                  const project = projects.find((item) => item.vuid === projectVuid);
                  return project ? `${project.project_number} — ${project.project_name}` : '';
                })()}
                placeholder="Choose a project"
                searchPlaceholder="Search projects"
                options={projects.map((project) => ({
                  vuid: project.vuid,
                  primary: `${project.project_number} — ${project.project_name}`,
                }))}
                onSelect={setProjectVuid}
              />
            </span>
          </label>
          <label className="text-sm font-medium text-gray-700">
            Accounting period
            <span className="mt-1 block font-normal">
              <AppSelect
                id="try-period"
                open={menu === 'try-period'}
                setOpen={setMenu}
                menuClassName="w-full"
                value={periodLabel(periods.find((period) => period.vuid === periodVuid))}
                placeholder="Choose a period"
                searchPlaceholder="Search periods"
                options={periods.map((period) => ({
                  vuid: period.vuid,
                  primary: periodLabel(period),
                }))}
                onSelect={setPeriodVuid}
              />
            </span>
          </label>
        </div>
        {parameters.filter((row) => row.key).map((row) => (
          <label key={row.id} className="block text-sm font-medium text-gray-700 mb-3">
            {row.label} for this test
            <span className="mt-1 flex items-center gap-2">
              <input
                className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-md"
                placeholder={row.display === 'percent' ? percentEntry(row.defaultValue) : (row.defaultValue || '')}
                value={previewParameters[row.key] || ''}
                onChange={(event) => setPreviewParameters((current) => ({ ...current, [row.key]: event.target.value }))}
              />
              {row.display === 'percent' ? <span className="text-sm text-gray-600">%</span> : null}
              {row.display === 'per_thousand' ? <span className="text-sm text-gray-600">per $1,000</span> : null}
            </span>
          </label>
        ))}

      {referencedScoped.length ? (
        <SourceScopeEditor
          projectVuid={projectVuid}
          projectCodesOnly={projectCodesOnly}
          costTypes={[]}
          budgetCombinations={budgetCombinations}
          groups={groups}
          expandedGroupIds={expandedGroupIds}
          setGroups={setGroups}
          setExpandedGroupIds={setExpandedGroupIds}
          intro={scopeOptional
            ? 'Owner billing includes every posted cost code. Add cost codes only to preview a smaller set. This is not saved on the library calculation.'
            : 'Used only for this preview. It is not saved on the library calculation.'}
          filterOptional={scopeOptional}
          showDestination={accountingLines.some((line) => line.strategy === 'cost_type_expense' || projectCostPolicy(line) !== 'never')}
          showCostCode={accountingLines.some((line) => projectCostPolicy(line) !== 'never')}
          destinationHint="Cost type resolves the journal account when a line uses the destination cost type. A cost code is required only when the line creates project cost."
        />
      ) : null}

      {referencedRunProviders.length ? (
        <section className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Period inputs</h2>
          <p className="text-sm text-gray-600 mb-4">Entered for this test only. These are not saved on the project.</p>
          {referencedRunProviders.map((provider) => (
            <label key={provider.key} className="block text-sm font-medium text-gray-700 mb-3">
              {provider.name}
              <input
                className="mt-1 w-full md:w-48 px-3 py-2 border border-gray-300 rounded-md"
                value={runInputValues[provider.key] || ''}
                onChange={(event) => setRunInputValues((current) => ({ ...current, [provider.key]: event.target.value }))}
              />
            </label>
          ))}
        </section>
      ) : null}

      {accountingLines.some((line) => projectCostPolicy(line) === 'project_choice') ? (
        <section className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Project Cost Treatment</h2>
          <p className="text-sm text-gray-600 mb-3">Used only for this test. It is not saved on a project.</p>
          {accountingLines.filter((line) => projectCostPolicy(line) === 'project_choice').map((line) => {
            const key = line.line_key || line.id;
            const amount = namedValues.find((item) => item.key === line.amount_key);
            return (
              <div key={key} className="mb-3 text-sm text-gray-800">
                <div className="font-medium mb-1">{line.side === 'credit' ? 'Credit' : 'Debit'} {amount?.label || line.amount_key || 'Calculated value'}</div>
                <label className="mr-4">
                  <input type="radio" name={`test-${key}`} checked={testJobCostChoices[key] === 'create_project_cost'} onChange={() => setTestJobCostChoices((current) => ({ ...current, [key]: 'create_project_cost' }))} /> Create Project Cost
                </label>
                <label>
                  <input type="radio" name={`test-${key}`} checked={testJobCostChoices[key] === 'journal_only'} onChange={() => setTestJobCostChoices((current) => ({ ...current, [key]: 'journal_only' }))} /> Journal Only
                </label>
              </div>
            );
          })}
        </section>
      ) : null}

      <button type="button" className="btn-claret mb-3" onClick={runPreview} disabled={!periodVuid || !projectVuid}>Run</button>
      {previewError ? <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{previewError}</div> : null}
      {preview ? <PreviewResult preview={preview} /> : null}
      </details>

      <details className="bg-white border border-gray-200 rounded-md p-4 mb-3">
        <summary className="text-sm font-semibold text-gray-900 cursor-pointer">Versions</summary>
        <p className="text-sm text-gray-600 mb-3">Published versions stay as they were. Open one to read its formula and accounting method.</p>
        <table className="min-w-full text-sm">
          <thead className="text-left text-gray-600">
            <tr>
              <th className="py-2 pr-4">Version</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2 pr-4">Published</th>
              <th className="py-2 pr-4" />
            </tr>
          </thead>
          <tbody>
            {[...(detail.versions || [])].reverse().map((version) => {
              const when = version.published_at ? new Date(version.published_at).toLocaleDateString() : '';
              const open = openVersion === version.vuid;
              const labels = {};
              (version.document?.parameters || []).forEach((item) => { labels[item.key] = item.label || item.key; });
              (version.document?.named_values || []).forEach((item) => { labels[item.key] = item.label || item.key; });
              const labelFor = (kind, key) => {
                if (kind === 'input') return basisTitle(key) || providers.find((item) => item.key === key)?.name || key;
                return labels[key] || key;
              };
              return (
                <React.Fragment key={version.vuid}>
                  <tr className="border-t border-gray-200">
                    <td className="py-2 pr-4">v{version.version_number}</td>
                    <td className="py-2 pr-4">{version.status === 'published' ? 'Published' : 'Draft'}</td>
                    <td className="py-2 pr-4">{when || '—'}</td>
                    <td className="py-2 pr-4">
                      <button type="button" className="underline" onClick={() => setOpenVersion(open ? '' : version.vuid)}>
                        {open ? 'Hide' : 'View'}
                      </button>
                    </td>
                  </tr>
                  {open ? (
                    <tr className="border-t border-gray-100">
                      <td className="py-3 pr-4 text-gray-800" colSpan={4}>
                        <div className="font-medium mb-1">Rates</div>
                        {(version.document?.parameters || []).length ? (version.document.parameters || []).map((item) => (
                          <div key={item.key}>{item.label}{item.description ? ` — ${item.description}` : ''}</div>
                        )) : <div>None</div>}
                        <div className="font-medium mt-3 mb-1">Calculation</div>
                        {(version.document?.named_values || []).map((item) => (
                          <div key={item.key}>{item.label}: {formulaExpression(item.formula, labelFor) || 'No formula'}</div>
                        ))}
                        <div className="font-medium mt-3 mb-1">Accounting</div>
                        {distributionSummary(version.distribution, version.document?.named_values || []).map((line, index) => (
                          <div key={`${line.side}-${index}`}>{line.side} {line.amount} — {line.account}{projectCostPhrase(line) ? ` — ${projectCostPhrase(line)}` : ''}</div>
                        ))}
                        {!((version.distribution || {}).lines || []).length ? <div>No accounting method on this version.</div> : null}
                        <div className="font-medium mt-3 mb-1">Projects on this version</div>
                        {(detail.usage || []).filter((row) => row.version_vuid === version.vuid).map((row) => (
                          <div key={row.assignment_vuid}>{row.project_number ? `${row.project_number} — ` : ''}{row.project_name}</div>
                        ))}
                        {!(detail.usage || []).some((row) => row.version_vuid === version.vuid) ? <div>No projects are on this version.</div> : null}
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </details>

      <details className="bg-white border border-gray-200 rounded-md p-4 mb-3">
        <summary className="text-sm font-semibold text-gray-900 cursor-pointer">Projects using this calculation</summary>
        {(detail.usage || []).length ? (
          <ul className="text-sm text-gray-800">
            {detail.usage.map((row) => (
              <li key={row.assignment_vuid}>{row.project_name} — v{row.version_number}{row.enabled ? '' : ' (disabled)'}</li>
            ))}
          </ul>
        ) : <p className="text-sm text-gray-600">No projects use this calculation yet.</p>}
      </details>

      {isAdmin ? (
        <details className="bg-white border border-gray-200 rounded-md p-4 mb-3">
          <summary className="text-sm font-semibold text-gray-900 cursor-pointer">Duplicate</summary>
          <form onSubmit={duplicate}>
          <p className="text-sm text-gray-600 mb-3">Creates a separate calculation and an editable draft. It is not linked to this one.</p>
          <div className="flex flex-wrap gap-3">
            <input
              className="px-3 py-2 border border-gray-300 rounded-md"
              placeholder="New name"
              value={duplicateName}
              onChange={(event) => setDuplicateName(event.target.value)}
              required
            />
            <button type="submit" className="btn-claret">Duplicate calculation</button>
          </div>
          </form>
        </details>
      ) : null}
      </div> : null}
    </div>
  );
}
